import { useContext } from 'react';
import { useQuery } from 'react-query';
import { ServerErrorResponse, baseApiUrl, fetcher } from 'data_sources/constants';
import { AppContext } from 'views/App/context';
import { CompletionTrendsResponse } from '../_types';
import { generateDTBTrends } from './riskPoolOrgDataGenerator';
import { useRiskPoolDemoData } from '../../../../_data_sources/useRiskPoolDemoData';

interface QueryParams {
  startDate: string;
  endDate: string;
  manualId?: string;
}

export const getAssignmentTrends = async (
  parentOrgId: string,
  childOrgId: string,
  queryParams: QueryParams,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<CompletionTrendsResponse> => {
  const url = `${baseApiUrl()}/activity-center/v1/organizations/${parentOrgId}/risk-pool/related-organizations/${childOrgId}/policy/assignments/completion-trends`;

  const params = new URLSearchParams();
  if (queryParams.startDate) params.append('startDate', queryParams.startDate);
  if (queryParams.endDate) params.append('endDate', queryParams.endDate);

  const queryString = params.toString();
  const fullUrl = queryString ? `${url}?${queryString}` : url;

  try {
    const data: CompletionTrendsResponse = await fetcher({
      method: 'get',
      url: fullUrl,
      options: { signal },
    });

    return data;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

export default function useAssignmentTrends(
  childOrgId: string,
  queryParams: QueryParams,
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const { currentOrganizationId } = useContext(AppContext);
  const parentOrgId = currentOrganizationId || childOrgId;
  const isDemoDataEnabled = useRiskPoolDemoData();

  return useQuery<CompletionTrendsResponse, Error>(
    [
      'risk_pool_org_completion_trends',
      parentOrgId,
      childOrgId,
      queryParams.startDate,
      queryParams.endDate,
      queryParams.manualId,
    ],
    async ({ signal }): Promise<CompletionTrendsResponse> => {
      try {
        if (isDemoDataEnabled) {
          const generated = generateDTBTrends(childOrgId, queryParams.startDate, queryParams.endDate);
          return {
            acknowledgements: generated.metrics.map((m) => ({
              date: m.period,
              percent: (m.assignmentsCompleted.onTime / Math.max(1, m.assignmentsCompleted.onTime + m.assignmentsCompleted.overdueByLessThanWeek + m.assignmentsCompleted.overdueByWeekOrMore + m.assignmentsNotCompleted.overdueByLessThanWeek + m.assignmentsNotCompleted.overdueByWeekOrMore)),
              count: m.assignmentsCompleted.onTime,
              totalCount: null,
            })),
            dtbs: generated.metrics.map((m) => ({
              date: m.period,
              percent: m.completedDTBs.onTime / Math.max(1, m.completedDTBs.onTime + m.completedDTBs.late),
              count: m.completedDTBs.onTime,
              totalCount: null,
            })),
          };
        }

        return await getAssignmentTrends(parentOrgId, childOrgId, queryParams, signal, onError);
      } catch (err) {
        if (onError) onError(err as ServerErrorResponse);
        throw err;
      }
    },
    {
      ...options,
      enabled: !!childOrgId && !!queryParams.startDate && !!queryParams.endDate,
    }
  );
}
